/**
 * Country Field Persistence Module
 *
 * Automatically saves and restores the user's country selection in event registration forms
 * using localStorage. This provides a better UX for users registering for multiple events.
 *
 * Features:
 * - Detects country dropdown field by label "Country"
 * - Saves selection to localStorage on first change
 * - Auto-fills country on subsequent visits
 * - Works with Indico's custom <ind-select> web component
 * - User-specific storage (keyed by user email)
 *
 * Technical Details:
 * - Uses custom web component: <ind-select data-input-type="country-dropdown">
 * - Dialog-based dropdown with <dialog> element
 * - Options stored as: <li role="option" data-value="Country Name">
 *
 * @module country_persistence
 */

(function() {
  'use strict';

  const STORAGE_KEY_PREFIX = 'ecmwf_indico_country_';
  const COUNTRY_LABEL_TEXT = 'country';
  const MAX_WAIT_ATTEMPTS = 20;
  const WAIT_INTERVAL = 500; // ms

  /**
   * Get the localStorage key for the current user
   * @returns {string|null} Storage key or null if user not authenticated
   */
  function getStorageKey() {
    // Try to get user email from Indico global variables
    if (window.Indico && window.Indico.User && window.Indico.User.email) {
      return STORAGE_KEY_PREFIX + window.Indico.User.email;
    }

    // Fallback: try to extract from page data
    const initialValues = getRegistrationFormData();
    if (initialValues && initialValues.email) {
      return STORAGE_KEY_PREFIX + initialValues.email;
    }

    // If no user info available, use a generic key (less ideal but works)
    console.warn('[Country Persistence] Could not identify user, using generic storage');
    return STORAGE_KEY_PREFIX + 'default';
  }

  /**
   * Get registration form data from the page
   * @returns {Object|null} Form data or null
   */
  function getRegistrationFormData() {
    const formContainer = document.querySelector('div[id*="registration-form"]');
    if (!formContainer) return null;

    try {
      const initialValues = formContainer.getAttribute('data-initial-values');
      return initialValues ? JSON.parse(initialValues) : null;
    } catch (e) {
      console.error('[Country Persistence] Error parsing form data:', e);
      return null;
    }
  }

  /**
   * Save country selection to localStorage
   * @param {string} countryName - The country display name
   */
  function saveCountrySelection(countryName) {
    const storageKey = getStorageKey();
    if (!storageKey) {
      console.warn('[Country Persistence] Cannot save: no storage key');
      return;
    }

    // Get country code from form data if available
    const countryKey = getCountryKeyFromName(countryName);

    const data = {
      countryKey: countryKey,
      countryName: countryName,
      timestamp: new Date().toISOString()
    };

    try {
      localStorage.setItem(storageKey, JSON.stringify(data));
      console.log('[Country Persistence] Saved:', countryName, countryKey ? '(' + countryKey + ')' : '');
    } catch (e) {
      console.error('[Country Persistence] Error saving to localStorage:', e);
    }
  }

  /**
   * Load saved country selection from localStorage
   * @returns {Object|null} Saved country data or null
   */
  function loadCountrySelection() {
    const storageKey = getStorageKey();
    if (!storageKey) return null;

    try {
      const data = localStorage.getItem(storageKey);
      if (data) {
        const parsed = JSON.parse(data);
        console.log('[Country Persistence] Loaded:', parsed.countryName, parsed.countryKey ? '(' + parsed.countryKey + ')' : '');
        return parsed;
      }
    } catch (e) {
      console.error('[Country Persistence] Error loading from localStorage:', e);
    }

    return null;
  }

  /**
   * Get country code from country name using form data
   * @param {string} countryName - The country name
   * @returns {string|null} Country code or null
   */
  function getCountryKeyFromName(countryName) {
    const formContainer = document.querySelector('div[id*="registration-form"]');
    if (!formContainer) return null;

    try {
      const formDataAttr = formContainer.getAttribute('data-form-data');
      if (!formDataAttr) return null;

      const formData = JSON.parse(formDataAttr);

      if (formData && formData.items) {
        for (let itemId in formData.items) {
          const item = formData.items[itemId];
          if (item.inputType === 'country' && item.choices) {
            for (let choice of item.choices) {
              if (choice.caption === countryName) {
                return choice.countryKey;
              }
            }
          }
        }
      }
    } catch (e) {
      console.error('[Country Persistence] Error extracting country code:', e);
    }

    return null;
  }

  /**
   * Find the country dropdown element (ind-select custom element)
   * @returns {Element|null} The ind-select element or null
   */
  function findCountryDropdown() {
    // First try: Find by data-input-type attribute
    let dropdown = document.querySelector('ind-select[data-input-type="country-dropdown"]');
    if (dropdown) return dropdown;

    // Second try: Find by label text
    const labels = document.querySelectorAll('label');
    for (let label of labels) {
      const labelText = label.textContent.toLowerCase().trim();
      if (labelText === COUNTRY_LABEL_TEXT) {
        // Find the ind-select element near this label
        const fieldContainer = label.closest('div.field');
        if (fieldContainer) {
          dropdown = fieldContainer.querySelector('ind-select');
          if (dropdown) return dropdown;
        }
      }
    }

    // Third try: Find by name attribute
    dropdown = document.querySelector('ind-select[name="country"]');
    if (dropdown) return dropdown;

    return null;
  }

  /**
   * Get the currently selected country from the dropdown
   * @param {Element} indSelect - The ind-select element
   * @returns {string|null} Country name or null
   */
  function getCurrentCountrySelection(indSelect) {
    // Check the value attribute
    const value = indSelect.getAttribute('value');
    if (value && value !== '' && value !== 'Select a choice') {
      return value;
    }

    // Check the caption element
    const caption = indSelect.querySelector('[data-caption="true"]');
    if (caption) {
      const text = caption.textContent.trim();
      if (text && text !== 'Select a choice') {
        return text;
      }
    }

    return null;
  }

  /**
   * Set the country selection in the dropdown
   * @param {Element} indSelect - The ind-select element
   * @param {string} countryName - The country name to select
   */
  function setCountrySelection(indSelect, countryName) {
    // Open the dialog
    indSelect.click();

    // Wait for dialog to open and populate
    setTimeout(function() {
      const dialog = indSelect.querySelector('dialog');
      if (!dialog) {
        console.warn('[Country Persistence] Dialog not found');
        return;
      }

      // Find all options
      const options = dialog.querySelectorAll('li[role="option"]');

      for (let option of options) {
        // Get the data-value attribute which contains the country name
        const optionValue = option.getAttribute('data-value');

        if (optionValue === countryName) {
          // Click the option to select it
          option.click();
          console.log('[Country Persistence] Auto-selected country:', countryName);

          // Close the dialog
          setTimeout(function() {
            if (dialog.open) {
              dialog.close();
            }
          }, 100);

          return;
        }
      }

      // If we get here, country wasn't found
      console.warn('[Country Persistence] Country not found in dropdown:', countryName);

      // Close dialog
      if (dialog.open) {
        dialog.close();
      }
    }, 300); // Give more time for dialog to fully render
  }

  /**
   * Watch for changes in the country selection
   * @param {Element} indSelect - The ind-select element
   */
  function watchCountryChanges(indSelect) {
    // Watch for value attribute changes
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const newValue = indSelect.getAttribute('value');
          if (newValue && newValue !== '' && newValue !== 'Select a choice') {
            console.log('[Country Persistence] Country changed to:', newValue);
            saveCountrySelection(newValue);
          }
        }
      });
    });

    // Start observing
    observer.observe(indSelect, {
      attributes: true,
      attributeFilter: ['value']
    });

    console.log('[Country Persistence] Now watching for country changes');
  }

  /**
   * Setup the country persistence functionality
   * Waits for the form to be fully loaded before initializing
   */
  function setupCountryPersistence() {
    let attempts = 0;

    const trySetup = function() {
      attempts++;

      // Check if we're on a registration form page
      const formContainer = document.querySelector('div[id*="registration-form"]');
      if (!formContainer) {
        if (attempts < MAX_WAIT_ATTEMPTS) {
          setTimeout(trySetup, WAIT_INTERVAL);
        }
        return;
      }

      // Find the country dropdown
      const indSelect = findCountryDropdown();
      if (!indSelect) {
        if (attempts < MAX_WAIT_ATTEMPTS) {
          setTimeout(trySetup, WAIT_INTERVAL);
        } else {
          console.log('[Country Persistence] No country field found in this form');
        }
        return;
      }

      console.log('[Country Persistence] Country field detected, initializing...');

      // Check if there's a saved country selection
      const savedCountry = loadCountrySelection();

      // Check current selection
      const currentSelection = getCurrentCountrySelection(indSelect);

      // If no current selection and we have a saved one, auto-fill it
      if (!currentSelection && savedCountry && savedCountry.countryName) {
        console.log('[Country Persistence] Auto-filling saved country...');
        setCountrySelection(indSelect, savedCountry.countryName);
      } else if (currentSelection) {
        console.log('[Country Persistence] Current selection:', currentSelection);
      }

      // Watch for changes to save new selections
      watchCountryChanges(indSelect);

      console.log('[Country Persistence] Initialization complete');
    };

    // Start the setup process
    trySetup();
  }

  /**
   * Initialize the module when DOM is ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupCountryPersistence);
  } else {
    setupCountryPersistence();
  }

})();
